import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { MdToday } from 'react-icons/md';
// components
// types
import { InventoryItem, CALENDAR_PLATFORM, CALENDAR_VIEW } from 'types';
// assets
import { BiLinkExternal } from 'react-icons/bi';
// utils
import { priceFormatter } from 'utils/price';
// queries
import { useCreateUserCalendarEventMutation } from 'queries/createEvent/createUserCalendarEventMutation';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  box-sizing: border-box;
  width: 384px;
  background: ${props => props.theme.colors.white[100]};
  border: 1px solid #eeeff2;
  border-radius: 7px;
  padding: 16px;
  margin-bottom: 16px;

  @media screen and (max-width: 520px) {
    border-radius: 0px;
    width: 100%;
    margin-bottom: 0;
    border: none;
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.black[100]};
  text-align: center;
  margin-bottom: 8px;
`;
const PriceWrapper = styled.div`
  padding-left: 104px;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  column-gap: 8px;
  color: ${({ theme }) => theme.colors.black[60]};
`;
const Price = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black[100]};
`;
const Row = styled.div<{ fontSize?: number }>`
  display: flex;
  column-gap: 8px;
  font-size: 14px;
  margin: 10px 0;
`;
const Label = styled.div`
  min-width: 96px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black[60]};
`;
const Value = styled.div`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const ExternalLinkWrapper = styled.a`
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  margin: 24px 0 8px;
`;
const Link = styled.div`
  margin-left: 4px;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black[100]};
`;

const fields = [
  {
    value: 'itemCondition',
    label: 'Condition',
  },
  {
    value: 'engine',
    label: 'Engine',
  },
  {
    value: 'make',
    label: 'Make',
  },
  {
    value: 'model',
    label: 'Model',
  },
  {
    value: 'trim',
    label: 'Trim',
  },
  {
    value: 'year',
    label: 'Year',
  },
  {
    value: 'mileage',
    label: 'Mileage',
  },
  {
    value: 'exteriorColor',
    label: 'Ext. Color',
  },
  {
    value: 'interiorColor',
    label: 'Int. Color',
  },
  {
    value: 'transmission',
    label: 'Transmission',
  },
  {
    value: 'vin',
    label: 'VIN',
  },
  {
    value: 'stockNumber',
    label: 'Stock #',
  },
  {
    value: 'manufacturer',
    label: 'Manufacturer',
  },
  {
    value: 'vehicleType',
    label: 'Vehicle Type',
  },
];

type Props = {
  item: InventoryItem;
  isPreview?: boolean;
  userCalendarPlatform?: number;
  calendarWebsiteUrl?: string;
  dealerSocketFormLink?: string;
  onChangeView: (view: CALENDAR_VIEW) => void;
  mainTextColor?: string;
  mainColor?: string;
};

export const ItemDetails = ({
  item,
  isPreview = false,
  userCalendarPlatform = 0,
  onChangeView,
  mainColor,
  mainTextColor,
  calendarWebsiteUrl = '',
  dealerSocketFormLink = '',
}: Props) => {
  const { mutate: createUserCalendarEventMutation } =
    useCreateUserCalendarEventMutation();
  const { colors } = useTheme();
  const { t } = useTranslation();

  const priceAvailable =
    !!item.price && !!parseInt(item.price) && !isNaN(parseInt(item.price));

  const goToWebsite = (url: string) => window.open(url, '_blank');

  const handleScheduleTestDriveClick = () => {
    if (!!dealerSocketFormLink) {
      createUserCalendarEventMutation({
        data: {
          sendEmailNotificationOnly: true,
        },
      });
      goToWebsite(dealerSocketFormLink);

      return;
    }

    onChangeView(CALENDAR_VIEW.SCHEDULING);
  };

  return (
    <Container className='font-roboto'>
      <Title>{item.title}</Title>
      <PriceWrapper>
        {priceAvailable && (
          <>
            {t('Price')} <Price>{priceFormatter(parseInt(item.price))}</Price>
          </>
        )}
        {!priceAvailable && <Price>{t('Please call for price')}</Price>}
      </PriceWrapper>
      {fields.map((field, i) => {
        const value = item?.[field.value as keyof InventoryItem];
        return value ? (
          <Row key={i}>
            <Label>{t(field.label)}</Label>
            <Value>{value}</Value>
          </Row>
        ) : null;
      })}
      {item.detailsPageUrl && (
        <ExternalLinkWrapper href={item.detailsPageUrl} target='_blank'>
          <BiLinkExternal size='18px' color={colors.black[100]} />
          <Link>{t('Vehicle Details Page')}</Link>
        </ExternalLinkWrapper>
      )}
      {((!!userCalendarPlatform &&
        userCalendarPlatform !== CALENDAR_PLATFORM.WEBSITE) ||
        !!dealerSocketFormLink) && (
        <Button
          disabled={isPreview}
          text={t('Schedule Test Drive')}
          icon={<MdToday color={colors.white[100]} />}
          onClick={handleScheduleTestDriveClick}
          style={{
            backgroundColor: mainColor,
            color: mainTextColor,
            borderColor: mainColor,
            width: '100%',
          }}
        />
      )}
      {!!userCalendarPlatform &&
        userCalendarPlatform === CALENDAR_PLATFORM.WEBSITE && (
          <Button
            text={t('Schedule Test Drive')}
            icon={<MdToday color={colors.white[100]} />}
            onClick={() => goToWebsite(calendarWebsiteUrl)}
            style={{
              backgroundColor: mainColor,
              color: mainTextColor,
              borderColor: mainColor,
              width: '100%',
            }}
          />
        )}
    </Container>
  );
};
